import React from 'react';
import styled from 'styled-components';

import { DEFAULT_TEXT_TRANSCRIPT_CONFIG } from '../../videoTranscriptionProcessor/constants/karaokeConstants';
import ColorDropdownSelect from '../common/ColorDropdownSelect';
import ItemDropdownSelect from '../common/ItemDropdownSelect';
import {
  Section as SectionBase,
  SectionCaption,
  SliderGroup,
} from './TextSettingsUI';

import type { KaraokeConfig } from '@src/videoTranscriptionProcessor/types/karaokeTypes';

type Props = {
  karaokeElement: KaraokeConfig;
  modifyProperty: (patch: Partial<KaraokeConfig>) => void;
};

export const TextTranscriptionSettingsUI: React.FC<Props> = ({
  karaokeElement,
  modifyProperty,
}) => {
  const handleSettingChange = (
    propertyName: string,
    value: string | number | null | undefined,
  ) => {
    const patch = {
      [propertyName]: value,
    } as Partial<KaraokeConfig>;
    modifyProperty(patch);
  };

  return (
    <Section>
      {/* Row 1 */}
      <SliderGroup>
        <Section style={{ maxWidth: '50%' }}>
          <SectionCaption>Active Color</SectionCaption>
          <ColorDropdownSelect
            action={handleSettingChange}
            propertyName="transcript_color"
            defaultValue={
              karaokeElement.transcript_color ||
              DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_color
            }
          />
        </Section>
      </SliderGroup>

      {/* Row 2 */}
      <SliderGroup>
        <Section>
          <SectionCaption>Effect</SectionCaption>
          <div>
            <ItemDropdownSelect
              action={(propertyName, value) =>
                handleSettingChange(propertyName, value || undefined)
              }
              propertyName="transcript_effect"
              defaultValue={
                (karaokeElement.transcript_effect as string) || null
              }
              values={[
                { value: null, caption: 'None' },
                { value: 'color', caption: 'Color' },
                { value: 'karaoke', caption: 'Karaoke' },
                { value: 'highlight', caption: 'Highlight' },
                { value: 'enlarge', caption: 'Enlarge' },
              ]}
            />
          </div>
        </Section>

        <Section>
          <SectionCaption>Enter Animation</SectionCaption>
          <div>
            <ItemDropdownSelect
              action={handleSettingChange}
              propertyName="transcript_placement"
              defaultValue={
                (karaokeElement.transcript_placement as string) ||
                DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_placement
              }
              values={[
                { value: 'static', caption: 'Total Line' },
                { value: 'animate', caption: 'Typerwriter' },
              ]}
            />
          </div>
        </Section>
      </SliderGroup>

      {/* Row 3 */}
      <SliderGroup>
        <Section>
          <SectionCaption>Words Per Line (override)</SectionCaption>
          <ItemDropdownSelect
            action={(propertyName, value) =>
              handleSettingChange(
                propertyName,
                value == null ? null : Number(value),
              )
            }
            propertyName="transcript_maximum_length"
            defaultValue={
              karaokeElement.transcript_maximum_length == null
                ? null
                : String(karaokeElement.transcript_maximum_length)
            }
            values={(() => {
              const values = [
                {
                  value: null,
                  caption: 'Default (Transcript Line Break)',
                },
                { value: '3', caption: '1 word per line' },
                { value: '8', caption: '2 words per line' },
                { value: '15', caption: '3 words per line' },
                { value: '20', caption: '5 words per line' },
              ];

              // in case value is custom (shouldn't happen), add it to the list
              if (
                values.every(
                  v => v.value != karaokeElement.transcript_maximum_length,
                )
              ) {
                values.push({
                  value: String(karaokeElement.transcript_maximum_length),
                  caption:
                    karaokeElement.transcript_maximum_length ==
                    DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_maximum_length
                      ? 'Default (Transcript Line Break)'
                      : `Custom length: ${karaokeElement.transcript_maximum_length}`,
                });
              }

              return values;
            })()}
          />
        </Section>
      </SliderGroup>
    </Section>
  );
};

export default TextTranscriptionSettingsUI;

const Section = styled(SectionBase)`
  flex: 1;
`;
